import { appAuthenticator, Logger } from '@futrli/shared-services';
import bigInt from 'big-integer';
import { publicIpv4 } from 'public-ip';

export interface CognitoUserData {
  username: string;
  attributes: {
    name: string;
    given_name: string;
    family_name: string;
    email: string;
    sub: string;
  };
}

export const getCurrentUser = async () => (await appAuthenticator.getCurrentAuthenticatedUser()) as CognitoUserData;

export const getCurrentUserId = async (): Promise<string | null> => {
  const currentUser = (await appAuthenticator.getCurrentAuthenticatedUser()) as CognitoUserData;

  return currentUser?.attributes.sub || null;
};

/**
 * Takes a given string and through various steps, picks
 * a bucket from the provided list of options.
 *
 * @param {string} guid
 * @param {string[]} buckets
 * @returns {string}
 */
export const getUserBucket = async (guid: string, buckets: string[]): Promise<string> => {
  const hexBase = 16;
  const buffer = new TextEncoder().encode(guid);
  const digest = await window.crypto.subtle.digest('SHA-1', buffer);
  const result = Array.from(new Uint8Array(digest))
    // eslint-disable-next-line no-magic-numbers
    .map(x => x.toString(hexBase).padStart(2, '0'))
    .join('');
  const index = bigInt(result, hexBase).divmod(buckets.length).remainder.toJSNumber();
  return buckets[index];
};

/**
 * It should take a user's uuid and generate an object with an
 * ABGroup and ABCGroup fields for sending to third party
 * tracking services.
 *
 * @param {CognitoUserData} user
 * @returns {object}
 */
export const getUserBuckets = async ({ username }: CognitoUserData): Promise<{ ABGroup: string; ABCGroup: string }> => {
  const ABGroup = await getUserBucket(username, ['A', 'B']);
  const ABCGroup = await getUserBucket(username, ['A', 'B', 'C']);
  return { ABGroup, ABCGroup };
};

/**
 * Gets the current user public IP
 * @returns {string|undefined}
 */
export const getUserPublicIP = async () => {
  try {
    return publicIpv4();
  } catch (error) {
    Logger.error(String(error));
    return;
  }
};

export const getCurrentUserFlagDetails = async (user: CognitoUserData) => {
  const ip = await getUserPublicIP();

  return {
    ip,
    key: user.attributes.sub,
    firstName: user.attributes.given_name,
    lastName: user.attributes.family_name,
    email: user.attributes.email,
    allAttributesPrivate: true
  };
};

/**
 * Checks if the user is authenticated.
 * @returns {boolean}
 */
export const isAuthenticatedUser = async (): Promise<boolean> => {
  try {
    const userId = await getCurrentUserId();
    return Boolean(userId);
  } catch {
    return false;
  }
};
